import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, CreditCard } from "lucide-react";
import { useMemo } from "react";
import { Transaction } from "@/lib/types";
import { useCurrency } from "@/hooks/useCurrency";

interface ExpensesCardProps {
  transactions: Transaction[];
}

/** Sum all expense transactions in the specified year + month (0-indexed). */
function monthlyExpenses(
  transactions: Transaction[],
  year: number,
  month: number
): number {
  return transactions
    .filter((t) => {
      const d = new Date(t.date);
      return (
        t.type === 'expense' &&
        d.getFullYear() === year &&
        d.getMonth() === month
      );
    })
    .reduce((sum, t) => sum + t.amount, 0);
}

export default function ExpensesCard({ transactions }: ExpensesCardProps) {
  const { fmt } = useCurrency();
  const { currentMonthExpenses, previousMonthExpenses } = useMemo(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();
    const prevMonth = curMonth === 0 ? 11 : curMonth - 1;
    const prevYear = curMonth === 0 ? curYear - 1 : curYear;

    return {
      currentMonthExpenses: monthlyExpenses(transactions, curYear, curMonth),
      previousMonthExpenses: monthlyExpenses(transactions, prevYear, prevMonth),
    };
  }, [transactions]);

  const changeAmount = currentMonthExpenses - previousMonthExpenses;
  // [T38P0] Use real prev-month figure; 0% when no previous expense data
  const changePercentage =
    previousMonthExpenses > 0
      ? ((changeAmount / previousMonthExpenses) * 100).toFixed(1)
      : '0.0';
  const isPositive = changeAmount < 0; // For expenses, a decrease is positive

  return (
    <Card
      data-section="expenses"
      data-testid="home-card-kpi"
      className="h-full min-h-0 flex flex-col transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
        <CardTitle className="text-sm font-medium">Expenses</CardTitle>
        <div className="p-2 bg-red-500/10 rounded-full">
          <CreditCard className="h-4 w-4 text-red-500" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden">
        <div className="text-lg sm:text-2xl font-bold truncate">
          {fmt(currentMonthExpenses)}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          {isPositive ? (
            <TrendingDown className="h-3 w-3 text-green-500" />
          ) : (
            <TrendingUp className="h-3 w-3 text-red-500" />
          )}
          <span className={isPositive ? "text-green-500" : "text-red-500"}>
            {changeAmount < 0 ? "" : "+"}
            {changePercentage}%
          </span>
          <span>from last month</span>
        </p>
      </CardContent>
    </Card>
  );
}
